import React, { PropsWithChildren, ReactNode } from 'react'
import { Switch, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { InfoTooltip } from '../../info-tooltip/info-tooltip'

type SettingToggle = {
    checked: boolean
    onChange: (checked: boolean) => void
    disabled?: boolean
}

type AppMessageSettingRowProps = {
    /** Omit for a row whose control needs the full width. */
    label?: ReactNode
    /** Explanatory copy, on an info icon after the label. */
    info?: ReactNode
    /**
     * Present when the setting is optional. The switch is the first thing in the
     * control column, and the settings it governs follow it on the same line.
     */
    toggle?: SettingToggle
    /** What happens while an optional setting is off, in place of its controls. */
    offText?: ReactNode
    htmlFor?: string
    className?: string
}

/**
 * One setting inside a behaviour card: its name on the left, its controls on the
 * right, on a single baseline.
 *
 * The name column is a fixed width so that every control in the card starts on
 * the same edge — a column of controls is scannable in a way that a ragged left
 * edge is not.
 *
 * Deliberately not a sentence with inputs embedded in it. A sentence has to be
 * rebuilt for every language it is translated into, and word order moves the
 * controls around with it; a label and a control survive translation unchanged.
 */
export const AppMessageSettingRow = ({
    label,
    info,
    toggle,
    offText,
    htmlFor,
    className,
    children,
}: PropsWithChildren<AppMessageSettingRowProps>) => {
    const isOn = !toggle || toggle.checked

    return (
        <div className={getClassNames('app-message-setting-row', className, { off: !isOn, unlabelled: !label })}>
            {(!!label || !!info) && (
                <div className="setting-row-label">
                    {toggle ? (
                        // A switch is a button, not a checkbox, so htmlFor cannot
                        // bind a label to it — the label has to toggle it directly.
                        <span
                            className="setting-row-label-text toggles"
                            role="presentation"
                            onClick={() => !toggle.disabled && toggle.onChange(!toggle.checked)}
                        >
                            {label}
                        </span>
                    ) : (
                        <label className="setting-row-label-text" htmlFor={htmlFor}>
                            {label}
                        </label>
                    )}
                    {!!info && <InfoTooltip title={info} about={typeof label === 'string' ? label : undefined} />}
                </div>
            )}

            <div className="setting-row-control">
                {!!toggle && (
                    <Switch
                        size="small"
                        checked={toggle.checked}
                        disabled={toggle.disabled}
                        onChange={(checked) => toggle.onChange(checked)}
                        aria-label={typeof label === 'string' ? label : undefined}
                    />
                )}
                {isOn ? children : !!offText && <span className="setting-row-off-text">{offText}</span>}
            </div>
        </div>
    )
}
